import {
  Alert,
  Box,
  Container,
  Button,
  Typography,
  Paper,
  TextField,
  Chip,
  Autocomplete,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import { useParams } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import LoadingWidget from "../../widgets/LoadingWidget";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const AssignUser = () => {
  const { id } = useParams();
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-subcategory/${id}`
  );

  const {
    data: customerIds,
    isLoading: isCsLoading,
    isError: isCsError,
    error: Cserror,
  } = useApiEp(`/api/customer-ids`);

  const {
    data: assignedUsers,
    isLoading: isAssignedLoading,
    isError: isAssignedError,
    error: assignedError,
  } = useApiEp(`/api/assigned-users?categoryId=${id}`);

  useEffect(() => {
    if (assignedUsers?.data) {
      setSelectedCustomerIds(assignedUsers.data);
    }
  }, [assignedUsers]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINT}/api/assign-user`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({
          categoryId: id,
          customerIds: selectedCustomerIds,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to update customer assignments");
      }
      const responseData = await response.json();
      toast.success(
        selectedCustomerIds.length === 0
          ? "All customer assignments cleared successfully!"
          : responseData.message
      );
    } catch (err) {
      toastError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || isCsLoading || isAssignedLoading) {
    return <LoadingWidget label="Loading..." />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  if (isCsError) {
    return <Alert severity="error">{getErrorResponse(Cserror)}</Alert>;
  }

  if (isAssignedError) {
    return <Alert severity="error">{getErrorResponse(assignedError)}</Alert>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <BackBtn />
      <PageTitle>Assign User for {data?.name}</PageTitle>

      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Assign Customers to {data?.name}
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          Search and select customer IDs to assign to this category. You can
          select multiple customers. Clear all selections to remove all
          assignments.
        </Typography>

        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          <Autocomplete
            multiple
            options={customerIds || []}
            value={selectedCustomerIds}
            onChange={(event, newValue) => {
              setSelectedCustomerIds(newValue);
            }}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer IDs"
                placeholder="Search customer IDs..."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: "primary.light",
                    color: "primary.contrastText",
                  }}
                />
              ))
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                paddingY: 0.5,
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              size="large"
              sx={{ minWidth: 180 }}
            >
              {isSubmitting
                ? "Updating..."
                : selectedCustomerIds.length === 0
                ? "Clear Assignments"
                : "Update Assignments"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {data?.columns?.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Category Columns
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {data.columns.map((column, index) => (
              <Chip
                key={index}
                label={column}
                sx={{
                  backgroundColor: "primary.light",
                  color: "primary.contrastText",
                }}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default AssignUser;
